import {
  del as amplifyDel,
  get as amplifyGet,
  patch as amplifyPatch,
  post as amplifyPost,
  put as amplifyPut,
} from 'aws-amplify/api';
import { Safari16API } from './Safari16API';

// Detect iOS 16 or lower, or Safari 16 or lower (Amplify API bugs)
// On iOS, all browsers (Chrome, Firefox, etc.) use WebKit engine, so they all have the same bug
const isSafari16OrLower = (() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';

  // Check for iOS version first (affects all browsers on iOS)
  const iOSMatch = ua.match(/OS ([\d_]+) like Mac OS X/);

  if (iOSMatch) {
    const iOSVersion = iOSMatch[1]?.replace(/_/g, '.') || '';
    const versionParts = iOSVersion.split('.') || [];
    const majorVersion = parseInt(versionParts[0], 10);

    // iOS 16.x or lower - all browsers affected
    if (majorVersion <= 16) {
      console.log('[ChiroUpAPI] Detected iOS 16 or lower:', iOSVersion);

      return true;
    }
  }

  // Check for desktop Safari version
  const safariMatch = ua.match(/Version\/([\d.]+)/) || [];

  if (safariMatch) {
    const versionParts = safariMatch[1]?.split('.') || [];
    const majorVersion = parseInt(versionParts[0], 10);

    // Safari 16.x or lower
    if (majorVersion <= 16) {
      console.log('[ChiroUpAPI] Detected Safari 16 or lower:', safariMatch[1]);

      return true;
    }
  }

  return false;
})();

console.log(
  '[ChiroUpAPI] Using Safari16 fallback:',
  isSafari16OrLower,
  '| UA:',
  typeof navigator !== 'undefined' ? navigator.userAgent.substring(0, 100) : 'N/A'
);

const get = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    console.log('[ChiroUpAPI.get] Routing to Safari16API fallback');
    return Safari16API.get(apiName, path, options);
  }
  console.log('[ChiroUpAPI.get] Using normal Amplify (not Safari 16)');

  if (options.queryParams) {
    // Get rid of any undefined values
    Object.entries(options.queryParams).forEach(([key, value]) => {
      if (value === undefined) {
        delete options.queryParams[key];
      }
    });
  }

  const restOperation = amplifyGet({
    apiName,
    path,
    options,
  });
  const response = await restOperation.response.catch(err => {
    let body = {};

    if (err?._response?.body) {
      body = JSON.parse(err._response.body);
    }

    if (!err?._response?.body && err?.message) {
      body = {
        message: err.message,
      };
    }

    // eslint-disable-next-line no-throw-literal
    throw {
      response: {
        data: {
          statusCode: err?._response?.statusCode || err?.$metadata?.httpStatusCode,
          ...body,
        },
      },
    };
  });

  // If the caller wants a blob, return the raw response without parsing.
  if (options.responseType === 'blob') {
    return response as unknown as T;
  }

  let returnResponse = null;

  if (response.body) {
    const json = await response.body.json().catch(() => {});
    returnResponse = json || null;
  }

  return returnResponse as T;
};

const put = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    return Safari16API.put(apiName, path, options);
  }

  const restOperation = amplifyPut({
    apiName,
    path,
    options,
  });
  const response = (await restOperation.response.catch(err => {
    let body = {};

    if (err?._response?.body) {
      body = JSON.parse(err._response.body);
    }

    if (!err?._response?.body && err?.message) {
      body = {
        message: err.message,
      };
    }

    // eslint-disable-next-line no-throw-literal
    throw {
      response: {
        data: {
          statusCode: err?._response?.statusCode || err?.$metadata?.httpStatusCode,
          ...body,
        },
      },
    };
  })) as any;

  let returnResponse = null;

  if (response.body) {
    const json = await response.body.json().catch(() => {});
    returnResponse = json || null;
  }

  return returnResponse as T;
};

const patch = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    return Safari16API.patch(apiName, path, options);
  }

  const restOperation = amplifyPatch({
    apiName,
    path,
    options,
  });
  const response = (await restOperation.response.catch(err => {
    let body = {};

    if (err?._response?.body) {
      body = JSON.parse(err._response.body);
    }

    if (!err?._response?.body && err?.message) {
      body = {
        message: err.message,
      };
    }

    // eslint-disable-next-line no-throw-literal
    throw {
      response: {
        data: {
          statusCode: err?._response?.statusCode || err?.$metadata?.httpStatusCode,
          ...body,
        },
      },
    };
  })) as any;
  let returnResponse = null;

  if (response.body) {
    const json = await response.body.json().catch(() => {});
    returnResponse = json || null;
  }

  return returnResponse as T;
};

const post = async <T = any>(
  apiName: any,
  path: any,
  options: any,
  requestOptions: {
    type?: 'json' | 'blob' | 'text';
  } = {}
): Promise<T> => {
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    return Safari16API.post(apiName, path, options, requestOptions);
  }

  const restOperation = amplifyPost({
    apiName,
    path,
    options,
  });
  const response = (await restOperation.response.catch(err => {
    let body = {};

    if (err?._response?.body) {
      body = JSON.parse(err._response.body);
    }

    if (!err?._response?.body && err?.message) {
      body = {
        message: err.message,
      };
    }

    if (typeof body === 'string') body = { message: body };
    // eslint-disable-next-line no-throw-literal
    throw {
      response: {
        data: {
          statusCode: err?._response?.statusCode || err?.$metadata?.httpStatusCode,
          ...body,
        },
      },
    };
  })) as any;
  let returnResponse = null;

  if (response.body) {
    let res = null;

    if (requestOptions?.type === 'blob') {
      res = await response.body.blob().catch(() => {});
    } else if (requestOptions?.type === 'text') {
      res = await response.body.text().catch(() => {});
    } else {
      res = await response.body.json().catch(() => {});
    }

    returnResponse = res;
  }

  return returnResponse as T;
};

const del = async <T = any>(apiName: any, path: any, options: any): Promise<T> => {
  // Use Safari16API fallback for Safari 16.0 or lower
  if (isSafari16OrLower) {
    return Safari16API.del(apiName, path, options);
  }

  if (options.body) {
    console.error(
      'DELETE requests cannot have a body via the amplify library in V6...smh...so this body is not going to be sent.',
      options.body
    );
  }

  const restOperation = amplifyDel({
    apiName,
    path,
    options,
  });
  const response = (await restOperation.response.catch(err => {
    let body = {};

    if (err?._response?.body) {
      body = JSON.parse(err._response.body);
    }

    if (!err?._response?.body && err?.message) {
      body = {
        message: err.message,
      };
    }

    // eslint-disable-next-line no-throw-literal
    throw {
      response: {
        data: {
          statusCode: err?._response?.statusCode || err?.$metadata?.httpStatusCode,
          ...body,
        },
      },
    };
  })) as any;
  let returnResponse: T | null = null;

  if (response.body) {
    const json = await response.body.json().catch(() => null);
    returnResponse = (json ?? null) as T;
  }

  return returnResponse as T;
};

export const ChiroUpAPI = {
  get,
  put,
  patch,
  post,
  del,
};
